"use client";

import Image from "next/image";
import { useState } from "react";
import { ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

type GalleryItem = {
  src: string;
  label: string;
};

function GalleryImage({
  item,
  priority = false,
  sizes,
}: {
  item: GalleryItem;
  priority?: boolean;
  sizes: string;
}) {
  return (
    <div className="break-inside-avoid mb-2">
      <div className="group relative overflow-hidden rounded-2xl shadow-md ring-1 ring-rose/10">
        <Image
          src={item.src}
          alt={`SkinSio Gallery - ${item.label}`}
          width={1200}
          height={900}
          sizes={sizes}
          style={{ width: "100%", height: "auto", display: "block" }}
          className="transition-transform duration-700 group-hover:scale-105"
          priority={priority}
        />
        {item.label && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/55 p-6 text-center opacity-0 backdrop-blur-[2px] transition-all duration-500 group-hover:opacity-100">
            <p className="mb-2 translate-y-3 transform text-2xl font-bold text-white transition-transform duration-500 group-hover:translate-y-0">
              {item.label}
            </p>
            <div className="bg-golden h-1 w-12 scale-x-0 transform transition-transform delay-100 duration-700 group-hover:scale-x-100" />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Exterior Images ──────────────────────────────────────────────────────────
const exteriorImages: GalleryItem[] = [
  { src: "/Gallery/Exterior1.webp", label: "Exterior View" },
  { src: "/Gallery/Exterior2.webp", label: "Exterior View" },
  { src: "/Gallery/Exterior3.webp", label: "Exterior View" },
  { src: "/Gallery/Entrance.webp",  label: "Clinic Entrance" },
];

// ─── Interior Images ──────────────────────────────────────────────────────────
const interiorImages: GalleryItem[] = [
  { src: "/Gallery/Reception.webp",       label: "Reception" },
  { src: "/Gallery/Hallway.webp",         label: "Clinic Hallway" },
  { src: "/Gallery/Consultation.webp",    label: "Consultation Room" },
  { src: "/Gallery/Therapy 1.webp",       label: "Therapy Area" },
  { src: "/Gallery/Therapy 2.webp",       label: "Therapy Area" },
  { src: "/Gallery/Therapy 3.webp",       label: "Therapy Area" },
  { src: "/Gallery/Therapy 4.webp",       label: "Therapy Area" },
  { src: "/Gallery/Dental.webp",          label: "Dental Suite" },
  { src: "/Gallery/Reception Close.webp", label: "Reception" },
];

const PREVIEW_COUNT = 2;

/**
 * Gallery Section Component
 * Masonry-columns layout — images show at their natural aspect ratio
 * (landscape stays landscape, portrait stays portrait) with no distortion.
 * Exterior and Interior are displayed as separate labelled groups, each
 * showing only the first 2 images by default. The rest are collapsed and
 * only mount into the DOM (i.e. only load) once "View All" is clicked.
 */
export function Gallery() {
  const [exteriorExpanded, setExteriorExpanded] = useState(false);
  const [interiorExpanded, setInteriorExpanded] = useState(false);

  const visibleExterior = exteriorExpanded
    ? exteriorImages
    : exteriorImages.slice(0, PREVIEW_COUNT);

  return (
    <section
      id="gallery"
      className="relative bg-background py-12 lg:py-16"
      aria-labelledby="gallery-title"
    >
      <div className="container mx-auto px-4">

        {/* ── Section Header ── */}
        <div className="mb-16 text-center">
          <div className="bg-golden/10 text-golden mb-6 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold tracking-wide uppercase">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            Our Gallery
          </div>
          <h2
            id="gallery-title"
            className="mb-6 text-4xl font-bold md:text-5xl"
          >
            Experience <span className="text-rose">SkinSio</span>
          </h2>
          <p className="text-muted-foreground mx-auto max-w-xl text-lg">
            Take a look at our state-of-the-art facility and the premium
            environment we&apos;ve created for your care.
          </p>
        </div>

        {/* ── Exterior ── */}
        <div className="mb-14">
          <h3 className="text-rose mb-5 text-center text-xs font-semibold uppercase tracking-[0.2em]">
            Exterior
          </h3>
          {/* Simple 2-col equal-height grid in preview mode avoids masonry
              balancing gaps; switches to columns-based masonry once all
              images are shown */}
          {exteriorExpanded ? (
            <div className="columns-1 gap-2 md:columns-2">
              {exteriorImages.map((item, i) => (
                <GalleryImage
                  key={i}
                  item={item}
                  priority={i < 2}
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {visibleExterior.map((item, i) => (
                <div
                  key={i}
                  className="group relative aspect-[4/3] overflow-hidden rounded-2xl shadow-md ring-1 ring-rose/10"
                >
                  <Image
                    src={item.src}
                    alt={`SkinSio Gallery - ${item.label}`}
                    fill
                    sizes="(max-width: 640px) 100vw, 50vw"
                    className="object-cover transition-transform duration-700 group-hover:scale-105"
                    priority={i < 2}
                  />
                  {item.label && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/55 p-6 text-center opacity-0 backdrop-blur-[2px] transition-all duration-500 group-hover:opacity-100">
                      <p className="mb-2 translate-y-3 transform text-2xl font-bold text-white transition-transform duration-500 group-hover:translate-y-0">
                        {item.label}
                      </p>
                      <div className="bg-golden h-1 w-12 scale-x-0 transform transition-transform delay-100 duration-700 group-hover:scale-x-100" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {exteriorImages.length > PREVIEW_COUNT && (
            <div className="mt-4 text-center">
              <Button
                variant="outline"
                onClick={() => setExteriorExpanded((prev) => !prev)}
                className="border-rose/30 text-rose hover:bg-rose/10 rounded-full"
              >
                {exteriorExpanded ? "Show Less" : "View All"}
                {exteriorExpanded ? (
                  <ChevronUp className="ml-2 h-4 w-4" />
                ) : (
                  <ChevronDown className="ml-2 h-4 w-4" />
                )}
              </Button>
            </div>
          )}
        </div>

        {/* ── Interior ── */}
        <div>
          <h3 className="text-golden mb-5 text-center text-xs font-semibold uppercase tracking-[0.2em]">
            Interior
          </h3>
          {/* Preview mode: 3 equal-height tiles side by side — guarantees no
              blank space and no lopsided cropping. Expanded mode switches to
              the full masonry layout. */}
          {interiorExpanded ? (
            <div className="columns-1 gap-2 md:columns-2 lg:columns-3">
              {interiorImages.map((item, i) => (
                <GalleryImage
                  key={i}
                  item={item}
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {[interiorImages[1], interiorImages[0], interiorImages[3]].map(
                (item, i) => (
                  <div
                    key={i}
                    className="group relative aspect-[4/3] overflow-hidden rounded-2xl shadow-md ring-1 ring-golden/15"
                  >
                    <Image
                      src={item.src}
                      alt={`SkinSio Gallery - ${item.label}`}
                      fill
                      sizes="(max-width: 640px) 100vw, 33vw"
                      className="object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {item.label && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/55 p-4 text-center opacity-0 backdrop-blur-[2px] transition-all duration-500 group-hover:opacity-100">
                        <p className="mb-1 translate-y-3 transform text-lg font-bold text-white transition-transform duration-500 group-hover:translate-y-0">
                          {item.label}
                        </p>
                        <div className="bg-golden h-1 w-10 scale-x-0 transform transition-transform delay-100 duration-700 group-hover:scale-x-100" />
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          )}
          {interiorImages.length > 3 && (
            <div className="mt-4 text-center">
              <Button
                variant="outline"
                onClick={() => setInteriorExpanded((prev) => !prev)}
                className="border-golden/30 text-golden hover:bg-golden/10 rounded-full"
              >
                {interiorExpanded ? "Show Less" : "View All"}
                {interiorExpanded ? (
                  <ChevronUp className="ml-2 h-4 w-4" />
                ) : (
                  <ChevronDown className="ml-2 h-4 w-4" />
                )}
              </Button>
            </div>
          )}
        </div>

      </div>

      {/* Blend the bottom edge of this section into whatever sits below it */}
      <div className="section-blend-bottom" />
    </section>
  );
}
